import React, { useState, useRef, useEffect, useContext } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import tw from 'twin.macro';
import { Button, Col, Input, InputRef, Modal, Row, Spin } from 'antd';
import { WhiteBackground, MainLogo } from 'assets/images';
import { LoadingOutlined } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';
import { TickOutlined } from 'assets/svg/TickOutlined';
import { clearLocalStorage } from 'utils/helperMethods';
import { getConfig, getScreenInfoFromUrl, sendNotification, storeTokenInLocalStorage } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { setUserProfileMixpanel, trackEventMixpanel } from 'utils/mixpanel';
import { messageConfig } from '../../config/appMessageConfig';
import { FETCH_SCOPE_ERROR, GLOBAL_NAV_NAVIGATION, LOGIN_USER } from 'utils/mixpanelEventNames';
import { CancelButton } from 'components/molecules/Filter/SaveSelectionPresetsModal';
import { loginErrors } from 'utils/Enums';
import { getSecondaryTenantLogo, getTenantLogo } from 'utils/tenantLogos';
import { syncClientVersionFromApi } from 'utils/appVersion';

export const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;
const errorHex = '#C23829';
const auth0UserDatabase = 'Username-Password-Authentication';
type inputType = string | undefined;

type inputValType = {
    email: inputType;
    password: inputType;
};
type RefObject = {
    focus: () => void;
};

const ModalStyled = styled(Modal)`
    .ant-modal-content {
        overflow: hidden;
        border-radius: 4px;
    }

    .ant-modal-close-x {
        line-height: 43px;
    }
    .ant-modal-header {
        border-bottom: none;
        padding-bottom: 10px;
    }
    .ant-modal-footer {
        border-top: none;
    }
`;

const StyledLoginButton = css`
    &.ant-btn-primary[disabled],
    &.ant-btn-primary[disabled]:active,
    &.ant-btn-primary[disabled]:focus,
    &.ant-btn-primary[disabled]:hover {
        color: white !important;
        background: #40a9ff !important;
    }
`;

const BrandLogo = styled.img`
    ${tw`h-full`}
`;

const checkRequired = (email: inputType, password: inputType) => {
    let emailRequiredValid = false;
    let passwordRequiredValid = false;
    if (email) emailRequiredValid = true;
    if (password) passwordRequiredValid = true;
    return {
        emailRequiredValid,
        passwordRequiredValid,
    };
};

//validate field
const validateFields = (email: inputType) => {
    if (email && /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email)) {
        return true;
    }

    return false;
};
type Props = {
    onLogout: Function;
    webAuth: auth0.WebAuth;
};

const fetchScopeUrl = process.env.REACT_APP_FETCH_SCOPE_URL || '';

const ssoIdpId =
    process.env.REACT_APP_AUTH0_SSO_CONNECTION_NAME === 'null' || !process.env.REACT_APP_AUTH0_SSO_CONNECTION_NAME
        ? null
        : process.env.REACT_APP_AUTH0_SSO_CONNECTION_NAME;

//MAIN  COMPONENT STARTS HERE
const Login = ({ onLogout, webAuth }: Props) => {
    const navigate = useNavigate();
    const route = useLocation();
    const { appConfig } = useContext(UserContext);
    const { commonConfig } = appConfig;
    const screens = getConfig('screens', appConfig);
    const clientName = commonConfig?.clientName;
    const showSecondaryLogo = commonConfig?.showSecondaryLogo;
    const tenantId = process.env.REACT_APP_TENANT_ID || '';
    const mainTenantLogo = getTenantLogo(tenantId);
    const secondaryTenantLogo = getSecondaryTenantLogo(tenantId);
    const homeRoute = `/${appConfig?.screenDetailsWithRoutes?.[0]?.path}`;
    const { state }: any = route;
    const [loading, setLoading] = useState<boolean>(false);
    const [resetPasswordLoading, setResetPasswordLoading] = useState<boolean>(false);
    const [resetPasswordEmail, setResetPasswordEmail] = useState<string>('');
    const [resetPasswordModalStatus, setResetPasswordModalStatus] = useState({ step: 0, show: false });
    const [fullPageLoading, setFullPageLoading] = useState<boolean>(
        localStorage.getItem('isAuthenticated') ? true : false,
    );
    const [errorMessage, setErrorMessage] = useState<null | string>(null);
    const [inputVal, setInputVal] = useState<any>({
        email: undefined,
        password: undefined,
    });
    const [emailErrorMessage, setEmailErrorMessage] = useState<string>('');
    const [passwordErrorMessage, setPasswordErrorMessage] = useState<string>('');

    const emailRef = useRef<InputRef | null>(null);
    const resetPasswordRef = useRef<any>();

    //onchange event for email and password
    const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>, type: string) => {
        const formattedVal = e.target.value;
        const inputValTemp = { ...inputVal, [type]: formattedVal };

        setInputVal(JSON.parse(JSON.stringify(inputValTemp)));
    };

    //called when clicked on login button
    const loginUser = () => {
        // let oktaAuth = initialOktaAuth(config)
        localStorage.setItem('login_clicked', 'true');
        localStorage.setItem('from', 'AUTH0');
        let emailValid;
        let { emailRequiredValid, passwordRequiredValid } = checkRequired(inputVal.email, inputVal.password)!;

        if (!emailRequiredValid) {
            setEmailErrorMessage('Please enter your email.');
        } else {
            emailValid = validateFields(inputVal.email);
            if (!emailValid) {
                setEmailErrorMessage('Please enter a valid email address.');
            }
        }
        if (!passwordRequiredValid) {
            setPasswordErrorMessage('Please enter your Password.');
        }
        if (emailRequiredValid && passwordRequiredValid && emailValid && !loading) {
            setEmailErrorMessage('');
            setPasswordErrorMessage('');
            setLoading(true);

            //auth0 api for login
            //once successful it will redirect to /callback route
            webAuth.login(
                {
                    realm: auth0UserDatabase,
                    username: inputVal.email,
                    password: inputVal.password,
                },
                (err) => {
                    if (err) {
                        console.error('Login Error:', err);
                        let message = loginErrors.GENERIC_LOGIN_ERROR;
                        if (err.code === 'access_denied' && err.description === 'Wrong email or password.') {
                            message = loginErrors.INVALID_CREDENTIALS;
                        }
                        setErrorMessage(message);
                        setLoading(false);
                        return;
                    }
                },
            );
        }
    };

    useEffect(() => {
        if (resetPasswordModalStatus?.step === 1) {
            setTimeout(() => {
                resetPasswordRef.current?.focus();
            }, 100);
        }
    }, [resetPasswordModalStatus]);

    const fetchScopeAndNavigate = async (accessToken: string, to: string, email: string) => {
        try {
            //MIXPANEL LOGIN EVENT
            const isLoginButtonClicked: boolean = JSON.parse(localStorage.getItem('login_clicked') || 'false');
            setUserProfileMixpanel();
            if (isLoginButtonClicked) {
                trackEventMixpanel(LOGIN_USER, { from: localStorage.getItem('from') });
                localStorage.removeItem('from'); //this is used to identify if login is triggered from sso or not
                localStorage.removeItem('login_clicked'); //remove local storage item to avoid multiple events being triggered
            }
            // Prepare request options
            const requestOptions = {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    caller: 'ddai', //doesnt have any effect
                    provider: 'auth0', //doesnt have any effect
                    token: `Bearer ${accessToken}`,
                    // token: `Bearer ${tempToken}`,
                }),
            };

            // Fetch scope data
            const response = await fetch(fetchScopeUrl, requestOptions);

            if (!response.ok) {
                throw new Error('Authorization Error');
            }

            const data = await response.json();

            // Set user access scope in storage
            localStorage.setItem('userAccessScope', JSON.stringify(data?.data?.user_scope));

            // Process navigation info from URL
            const { childLabel, parentLabel } = getScreenInfoFromUrl(to, screens);

            // Track navigation in Mixpanel
            trackEventMixpanel(GLOBAL_NAV_NAVIGATION, {
                screen: childLabel,
                screenHeader: parentLabel,
                url: `${to}`,
            });
            // Navigate to desired route
            navigate(to, { replace: true });
        } catch (error: any) {
            // Log error details and call logout function
            trackEventMixpanel(FETCH_SCOPE_ERROR, {
                errorMsg: error?.message,
                error: String(error),
            });
            onLogout();
        }
    };

    //after successful authentication on redirection
    //not for refresh
    useEffect(() => {
        if (route.pathname.includes('callback')) {
            const to = state ? state?.path : homeRoute;

            setFullPageLoading(true);

            webAuth.parseHash((err, authResult) => {
                if (err) {
                    localStorage.clear();
                    console.log(err);
                    return;
                }
                if (authResult && authResult.accessToken && authResult.idToken) {
                    storeTokenInLocalStorage(authResult);
                    // Handle successful login
                    localStorage.setItem('isAuthenticated', 'true');
                    localStorage.setItem('email', authResult.idTokenPayload?.email || '');
                    syncClientVersionFromApi(authResult.accessToken).catch(() => null);
                    fetchScopeAndNavigate(authResult.accessToken, to, authResult.idTokenPayload?.email);
                }
            });
        }
    }, []);

    //when user refreshes the application
    useEffect(() => {
        if (localStorage.getItem('isAuthenticated')) {
            const to = state ? state?.path : homeRoute;

            setFullPageLoading(true);
            //check session checks if a valid session exists and increases the expiry as well
            webAuth.checkSession({}, (err, authResult) => {
                if (err) {
                    console.error('Error renewing token', err);
                    onLogout();
                    setFullPageLoading(false);
                    return;
                }

                if (authResult && authResult.accessToken && authResult.idToken) {
                    storeTokenInLocalStorage(authResult);
                    localStorage.setItem('email', authResult?.idTokenPayload?.email || '');
                    syncClientVersionFromApi(authResult.accessToken).catch(() => null);
                    fetchScopeAndNavigate(authResult.accessToken, to, authResult.email);
                }
            });
        }
    }, []);

    if (fullPageLoading) {
        return (
            <div className="h-screen w-screen">
                <Spin
                    tip="Authenticating..."
                    style={{
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}
                />
            </div>
        );
    }

    //reset password button click handler
    function handleResetButton() {
        const isValidEmail = validateFields(resetPasswordEmail);
        if (!isValidEmail) return sendNotification('error', 'Please enter a valid email address.');
        setResetPasswordLoading(true);

        webAuth.changePassword(
            {
                connection: auth0UserDatabase,
                email: resetPasswordEmail,
            },
            function (err, resp) {
                if (err) {
                    console.error('Error:', err.description);
                    sendNotification('error', err.description);
                } else {
                    setResetPasswordModalStatus({ ...resetPasswordModalStatus, step: 2, show: true });
                    // localStorage.setItem('resetEmail', resetPasswordEmail);
                }
                setResetPasswordLoading(false);
            },
        );
    }

    //called when clicked on sso button
    function handleSSOLogin() {
        if (!ssoIdpId) return;
        webAuth.authorize({
            connection: ssoIdpId ?? '', //this will be different for different customers
        });
    }

    return (
        <Row
            style={{
                backgroundImage: `url(${WhiteBackground}), linear-gradient(112.35deg, #9434F7 6.44%, #8D32F0 10.89%, #1E217D 95.4%)`,
                backgroundPosition: 'right',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                height: '100vh',
            }}
        >
            <div className="flex items-center relative top-6 right-4 justify-end gap-6 w-full h-fit">
                {showSecondaryLogo ? (
                    <>
                        <img
                            alt="logo"
                            src={secondaryTenantLogo}
                            className="right-10 top-6 h-[48px]"
                        />
                        <p className="h-[76px] border-r-2 border-borderGreyAlt"></p>
                    </>
                ) : null}
                <img src={MainLogo} className=" right-4 top-6 h-[48px]" />
            </div>
            <BrandLogo
                src={mainTenantLogo}
                style={{
                    height: '8%',
                    position: 'absolute',
                    top: '44%',
                    left: '25%',
                }}
                alt="logo"
            />
            <Col span={9} offset={15} className="windowHeight">
                <Row
                    className="pl-10"
                    // gutter={[20, 0]}
                    style={{
                        paddingLeft: '-10px',
                        color: '#243e51',
                        fontSize: '36px',
                        // marginTop: '50%',
                    }}
                >
                    <Col className="ml-[-2px]">
                        <span
                            style={{
                                color: colorConstants.violet,
                                fontSize: '36px',
                                display: 'inline-block',
                                fontWeight: 300,
                            }}
                        >
                            {appConfig?.commonConfig?.loginPageTextPart1}
                        </span>
                        <span
                            className="welcomeMsgStyleText"
                            style={{
                                color: colorConstants.violet,
                                fontWeight: '500',
                                fontSize: '36px',
                                display: 'block',
                            }}
                        >
                            {appConfig?.commonConfig?.loginPageTextPart2}
                        </span>
                    </Col>
                </Row>
                <Row
                    className="loginMsgStyle pl-10"
                    style={{
                        // paddingLeft: '2.3vw',
                        color: colorConstants.violet,
                        fontWeight: '400',
                        margin: '2px',
                        fontSize: '24px',
                    }}
                >
                    {messageConfig.login.accountLoginMsg}
                </Row>
                {ssoIdpId && (
                    <Row className="pl-10 mt-4">
                        <Col style={{ maxWidth: 'unset', flex: 'unset' }} span={8}>
                            <CancelButton
                                className=""
                                style={{
                                    padding: '10px 10px',
                                    border: `1px solid ${colorConstants.violet}`,
                                    borderRadius: '4px',
                                    background: colorConstants.white,
                                    color: colorConstants.violet,
                                    width: '350px',
                                    marginBottom: '5%',
                                }}
                            >
                                <a
                                    onClick={() => {
                                        localStorage.setItem('from', 'SSO');
                                        localStorage.setItem('login_clicked', 'true');
                                        handleSSOLogin();
                                    }}
                                    className="font-xs hover:opacity-90 text-purpleFont font-bold cursor-pointer"
                                >
                                    Sign in using {clientName} SSO
                                </a>
                            </CancelButton>
                        </Col>
                    </Row>
                )}
                {ssoIdpId && (
                    <Row className="pl-10">
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <div
                                style={{
                                    borderTop: `1px solid ${colorConstants.darkGrey}`,
                                    width: '155px',
                                    marginRight: '10px',
                                }}
                            ></div>
                            <div style={{ color: colorConstants.darkGrey }}>or</div>
                            <div
                                style={{
                                    borderTop: `1px solid ${colorConstants.darkGrey}`,
                                    width: '158px',
                                    marginLeft: '10px',
                                }}
                            ></div>
                        </div>
                    </Row>
                )}
                <Row className="pl-10 mt-2 font-sm" style={{ color: colorConstants.darkGrey, fontWeight: 500 }}>
                    Email ID
                </Row>
                <Row className="pl-10 mt-1">
                    <Col span={9}>
                        <Input
                            ref={emailRef}
                            placeholder={`Enter your Email ID`}
                            onChange={(e) => {
                                inputOnChange(e, 'email');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    loginUser();
                                }
                            }}
                            style={{
                                padding: '10px 10px',
                                border: `1px solid ${colorConstants.violet}`,
                                borderRadius: '4px',
                                background: colorConstants.white,
                                color: '#243E51',
                                width: '350px',
                                marginBottom: '5%',
                            }}
                        ></Input>
                    </Col>
                </Row>
                {emailErrorMessage && (
                    <Row className="pl-10" style={{ color: errorHex }}>
                        {emailErrorMessage}
                    </Row>
                )}
                <Row className="pl-10 mt-2 font-sm" style={{ color: colorConstants.darkGrey, fontWeight: 500 }}>
                    Password
                </Row>
                <Row className="pl-10 mt-1">
                    <Col span={9}>
                        <Input
                            placeholder={`Enter your Password`}
                            type="password"
                            onChange={(e) => {
                                inputOnChange(e, 'password');
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    loginUser();
                                }
                            }}
                            style={{
                                padding: '10px 10px',
                                border: `1px solid ${colorConstants.violet}`,
                                borderRadius: '4px',
                                background: colorConstants.white,
                                color: '#243E51',
                                width: '350px',
                                marginBottom: '5%',
                            }}
                        />
                    </Col>
                </Row>
                {passwordErrorMessage && (
                    <Row className="pl-10" style={{ color: errorHex }}>
                        {passwordErrorMessage}
                    </Row>
                )}
                {errorMessage && (
                    <Row className="pl-10" style={{ paddingTop: '5px', color: errorHex }}>
                        {errorMessage}
                    </Row>
                )}
                <Row className="pl-10 items-center mt-4 gap-4">
                    <Col style={{ maxWidth: 'unset', flex: 'unset' }} span={8}>
                        <Button
                            className={StyledLoginButton}
                            disabled={loading}
                            type="primary"
                            onClick={() => {
                                loginUser();
                            }}
                            style={{
                                color: colorConstants.white,
                                borderRadius: '4px',
                                backgroundColor: colorConstants.violet,
                                padding: '20px 50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 500,
                                width: '350px',
                            }}
                        >
                            {loading ? (
                                <Spin indicator={antIcon} style={{ color: 'white' }} />
                            ) : (
                                messageConfig.login.loginButtonText
                            )}
                        </Button>
                    </Col>
                </Row>
                <Row className="pl-10 mt-4">
                    <Col>
                        <p
                            onClick={() =>
                                setResetPasswordModalStatus({ ...resetPasswordModalStatus, step: 1, show: true })
                            }
                            className="text-purpleFont  font-sm cursor-pointer"
                        >
                            Forgot password?
                        </p>
                    </Col>
                </Row>
            </Col>
            <ModalStyled
                destroyOnClose
                maskClosable={resetPasswordModalStatus.step === 2 ? true : false}
                bodyStyle={{ paddingBlock: 6 }}
                onCancel={() => {
                    setResetPasswordEmail('');
                    setResetPasswordModalStatus({ ...resetPasswordModalStatus, step: 0, show: false });
                }}
                centered
                width={348}
                open={resetPasswordModalStatus.show}
                title={resetPasswordModalStatus.step === 1 ? 'Reset Password' : ''}
                footer={
                    resetPasswordModalStatus.step === 1 ? (
                        <Button
                            disabled={resetPasswordLoading}
                            style={{
                                color: colorConstants.white,
                                borderRadius: '4px',
                                backgroundColor: colorConstants.violet,
                                height: 40,
                                marginRight: 8,
                                fontWeight: 700,
                                minWidth: 132,
                                // paddingBlock: 12,
                                // display: 'flex',
                                // alignItems: 'center',
                                // justifyContent: 'center',
                            }}
                            className="!text-[13px]"
                            onClick={handleResetButton}
                        >
                            {resetPasswordLoading ? (
                                <Spin indicator={antIcon} style={{ color: 'white' }} />
                            ) : (
                                'Send Reset Link'
                            )}
                        </Button>
                    ) : (
                        ''
                    )
                }
            >
                <>
                    {resetPasswordModalStatus.step === 1 ? (
                        <>
                            <div className="font-xs text-promoColour">
                                To reset your password, enter the email address you use to sign in.
                            </div>
                            <div className="mt-4">
                                <Input
                                    ref={resetPasswordRef}
                                    placeholder="Enter your Email"
                                    type={'text'}
                                    value={resetPasswordEmail}
                                    onChange={(e) => setResetPasswordEmail(e.target.value)}
                                    style={{
                                        padding: '8px 10px',
                                        border: '1px solid #243E51',
                                        borderRadius: '4px',
                                        background: '#ffffff',
                                        color: '#243E51',
                                        width: '100%',
                                    }}
                                />
                            </div>
                        </>
                    ) : resetPasswordModalStatus.step === 2 ? (
                        <div className="flex flex-col items-center justify-center mt-6">
                            <span className="mb-4">
                                <TickOutlined />
                            </span>

                            <h2 className="font-lg text-subHeaderColor font-medium">Password Link Sent</h2>
                            {
                                <p className="text-fontGreyAlt1 font-xs mb-4">
                                    Please check your inbox {resetPasswordEmail}
                                </p>
                            }
                            <div className="w-full border border-promoColour mb-2"></div>
                            <p className="text-promoColour font-xs mb-4">
                                Unsure if that email address was correct?{' '}
                                <span
                                    onClick={() => {
                                        setResetPasswordModalStatus({ ...resetPasswordModalStatus, step: 1 });
                                    }}
                                    className="text-purpleFont font-medium cursor-pointer"
                                >
                                    Resend.
                                </span>
                            </p>
                        </div>
                    ) : null}
                </>
            </ModalStyled>
        </Row>
    );
};

export default Login;
